import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { platformAPI } from "../services/api";
import { telLink, whatsappLink } from "../utils/contact";

const percent = (step) => (step.rate === null ? "—" : `${Math.round(step.rate * 100)}%`);

const Step = ({ label, step, hint }) => (
  <div className="bg-white rounded-xl shadow p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-3xl font-bold text-gray-900">{percent(step)}</p>
    <p className="text-xs text-gray-400">
      {step.eligible === 0 ? "too early to tell" : `${step.count} of ${step.eligible} shops`}
    </p>
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const REASONS = {
  no_product: "No product yet",
  no_sale: "No sale yet",
};

/**
 * Where new shops get stuck: the funnel, signups per week, and the operator's
 * outreach queue. Everything comes from the platform's own tables.
 */
export default function Activation() {
  const [source, setSource] = useState("all");
  const [funnel, setFunnel] = useState(null);
  const [stuck, setStuck] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    setFunnel(null);
    platformAPI
      .activationFunnel({ source })
      .then(({ data }) => setFunnel(data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load the funnel"));
  }, [source]);

  useEffect(() => {
    platformAPI
      .stuckShops()
      .then(({ data }) => setStuck(data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load the shops"));
  }, []);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Activation</h1>
        <select
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          value={source}
          onChange={(e) => setSource(e.target.value)}
        >
          <option value="all">All shops</option>
          <option value="self">Signed up on their own</option>
          <option value="operator">Created by us</option>
        </select>
      </div>

      {error && <p className="text-red-600 mb-4">{error}</p>}

      {funnel && (
        <>
          <p className="text-sm text-gray-500 mb-3">
            Shops that signed up in the last {funnel.weeks} weeks ({funnel.signups}). A rate counts only the shops old
            enough to have finished that step.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Step label="Product within 24 h" step={funnel.product_within_24h} />
            <Step label="First sale within 7 days" step={funnel.sale_within_7d} />
            <Step label="Still selling in week 2" step={funnel.selling_in_week_2} />
            <Step label="Still selling in week 4" step={funnel.selling_in_week_4} />
            <Step label="Trial to paid" step={funnel.trial_to_paid} hint="paid = active today" />
          </div>

          <div className="bg-white rounded-xl shadow p-5 mb-6">
            <h2 className="font-semibold text-gray-800 mb-4">Signups per week</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={funnel.signups_per_week}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week_start" tickFormatter={(value) => value.slice(5)} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="signups" fill="#0284c7" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      <div className="bg-white rounded-xl shadow">
        <div className="px-5 py-3 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Stuck shops ({stuck.length})</h2>
          <p className="text-xs text-gray-500">
            Signed up over 48 hours ago with no product, or over 7 days ago with no sale. Your outreach queue.
          </p>
        </div>

        {stuck.length === 0 ? (
          <p className="px-5 py-8 text-center text-gray-400 text-sm">Nobody is stuck.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {stuck.map((shop) => (
              <li key={shop.id} className="px-5 py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/shops/${shop.id}`} className="font-medium text-primary-700 hover:underline">
                    {shop.name}
                  </Link>
                  <span className="ms-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    {REASONS[shop.reason]}
                  </span>
                  <p className="text-sm text-gray-500">
                    {shop.owner_name} · {shop.owner_phone} · {shop.days_since_signup} days since signup
                    {!shop.email_verified && " · email not confirmed"}
                  </p>
                  {shop.open_reports > 0 && (
                    <Link to="/problem-reports" className="text-xs font-medium text-red-700 hover:underline">
                      🐞 {shop.open_reports} open problem report{shop.open_reports === 1 ? "" : "s"}
                    </Link>
                  )}
                </div>
                {shop.owner_phone && (
                  <div className="flex gap-2">
                    <a href={telLink(shop.owner_phone)} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm">
                      📞 Call
                    </a>
                    <a
                      href={whatsappLink(
                        shop.owner_phone,
                        shop.reason === "no_product"
                          ? `Hello ${shop.owner_name}, can I help you add your products to ${shop.name}?`
                          : `Hello ${shop.owner_name}, can I help you ring up your first sale at ${shop.name}?`,
                      )}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-green-100 text-green-800 text-sm"
                    >
                      💬 WhatsApp
                    </a>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
